import { useMemo } from 'react';
import { trackEvent } from '@reverbdotcom/commons/src/elog/mparticle_tracker';
import { MParticleEventName, SellerRecommendationClickedEvent } from '@reverbdotcom/commons/src/elog/mparticle_types';
import { useSeller } from '@reverbdotcom/commons/src/user_hooks';
import { useViewTracking } from '@reverbdotcom/commons/src/use_tracking';

export const INSIGHT = {
  TOP_PICK_ELIGIBLE: 'top_pick_eligible',
  HIGH_COMPETITION_NEW: 'high_competition_new',
  PRICED_ABOVE_AVERAGE: 'priced_above_average',
  TOP_PICK_WINNER: 'top_pick_winner',
  RENEW_ELIGIBLE: 'renew_eligible',
  DIRECT_OFFER: 'direct_offer',
} as const;

export type Insight = (typeof INSIGHT)[keyof typeof INSIGHT];

export const CTA_TYPE = {
  PROMOTE_WITH_BUMP: 'promote_with_bump',
  ADJUST_PRICE: 'adjust_price',
  VIEW_PRODUCT_PAGE: 'view_product_page',
  RENEW: 'renew',
  SEND_DIRECT_OFFERS: 'send_direct_offers',
} as const;

export type CtaType = (typeof CTA_TYPE)[keyof typeof CTA_TYPE];

export const RECOMMENDED_ACTION = {
  INCREASE_BUMP_BID: 'increase_bump_bid',
  PRICE_DROP: 'price_drop',
  DIRECT_OFFER: 'direct_offer',
  NONE: 'none',
  RENEW: 'renew',
} as const;

export type RecommendedAction = (typeof RECOMMENDED_ACTION)[keyof typeof RECOMMENDED_ACTION];

interface InsightMeta {
  recommendedAction: RecommendedAction;
  componentName: string;
}

export const INSIGHT_META: Record<Insight, InsightMeta> = {
  [INSIGHT.TOP_PICK_ELIGIBLE]: { recommendedAction: RECOMMENDED_ACTION.INCREASE_BUMP_BID, componentName: 'TopPickEligiblePresentment' },
  [INSIGHT.HIGH_COMPETITION_NEW]: { recommendedAction: RECOMMENDED_ACTION.INCREASE_BUMP_BID, componentName: 'LowVisibilityPresentment' },
  [INSIGHT.PRICED_ABOVE_AVERAGE]: { recommendedAction: RECOMMENDED_ACTION.PRICE_DROP, componentName: 'PriceDropRecommendationPresentment' },
  [INSIGHT.TOP_PICK_WINNER]: { recommendedAction: RECOMMENDED_ACTION.NONE, componentName: 'TopPickWinnerPresentment' },
  [INSIGHT.RENEW_ELIGIBLE]: { recommendedAction: RECOMMENDED_ACTION.RENEW, componentName: 'RenewListingPresentment' },
  [INSIGHT.DIRECT_OFFER]: { recommendedAction: RECOMMENDED_ACTION.DIRECT_OFFER, componentName: 'NBADirectOffersPresentment' },
};

export interface TrackingContext {
  sellerId: string;
  shopId: string;
  secondaryUserModeIsActive: boolean;
  secondaryUserModeIsAvailable: boolean;
}

// Seller context for the tracking events. Callable from any presentment; the
// returned object is passed into the imperative track* helpers below
export function useTrackingContext(): TrackingContext {
  const { sellerId, shopId, secondaryUserModeIsActive, secondaryUserModeIsAvailable } = useSeller();

  return useMemo(
    () => ({
      sellerId: String(sellerId),
      shopId: String(shopId),
      secondaryUserModeIsActive: !!secondaryUserModeIsActive,
      secondaryUserModeIsAvailable: !!secondaryUserModeIsAvailable,
    }),
    [sellerId, shopId, secondaryUserModeIsActive, secondaryUserModeIsAvailable],
  );
}

export interface RecommendationDetails {
  recommendedBumpRate?: number;    // %, for increase_bump_bid insights
  recommendedPriceCents?: number;  // cents, for price_drop insights
}

export function useTrackImpression(insight: Insight, listingId: string, details?: RecommendationDetails) {
  const meta = INSIGHT_META[insight];
  const context = useTrackingContext();

  useViewTracking(
    {
      eventName: MParticleEventName.SellerRecommendationShown,
      componentName: meta.componentName,
      insight,
      recommendedAction: meta.recommendedAction,
      listingId,
      ...details,
      ...context,
    },
    true,
  );
}

export function buildClickedEvent(
  insight: Insight,
  listingId: string,
  ctaType: CtaType,
  context: TrackingContext,
): SellerRecommendationClickedEvent {
  const meta = INSIGHT_META[insight];
  return {
    eventName: MParticleEventName.SellerRecommendationClicked,
    componentName: meta.componentName,
    insight,
    recommendedAction: meta.recommendedAction,
    ctaType,
    listingId,
    ...context,
  };
}

export function trackClicked(
  insight: Insight,
  listingId: string,
  ctaType: CtaType,
  context: TrackingContext,
) {
  trackEvent(buildClickedEvent(insight, listingId, ctaType, context));
}

export function trackFollowed(
  insight: Insight,
  listingId: string,
  context: TrackingContext,
) {
  const meta = INSIGHT_META[insight];
  trackEvent({
    eventName: MParticleEventName.SellerRecommendationFollowed,
    componentName: meta.componentName,
    insight,
    recommendedAction: meta.recommendedAction,
    listingId,
    ...context,
  });
}
